import { escapeHtml } from './HTMLSanitizer';

const FLAG_EMOJI_REGEX = /[\u{1F1E6}-\u{1F1FF}]{2}/u;
const FLAG_EMOJI_REGEX_GLOBAL = /[\u{1F1E6}-\u{1F1FF}]{2}/gu;
const FLAG_OFFSET = 127397;

export const toCountryCode = flagEmoji => {
  const chars = Array.from(flagEmoji);
  const codePoints = chars.map(char => char.codePointAt(0) - FLAG_OFFSET);
  return String.fromCharCode(...codePoints);
};

export const isWindowsPlatform = () => {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform || navigator.userAgent || '';
  return /win/i.test(platform);
};

// Single source of truth for the flag-icons class used everywhere a flag is
// rendered as an icon (message content via buildFlagNode, phone inputs via
// CountryFlag.vue). Accepts an ISO country code (e.g. "BR").
// `fis` = squared (1x1) variant, which the `.flag-emoji` rule rounds into a
// circle. Swap the class recipe here to restyle every flag icon at once.
export const flagIconClass = countryCode =>
  `fi fi-${countryCode.toLowerCase()} flag-emoji`;

const buildFlagNode = flagEmoji => {
  const code = toCountryCode(flagEmoji).toLowerCase();
  const element = document.createElement('span');
  element.className = flagIconClass(code);
  element.setAttribute('title', code.toUpperCase());
  element.setAttribute('aria-label', code.toUpperCase());
  return element;
};

const replaceFlagEmojisInTextNode = textNode => {
  const text = textNode.nodeValue;
  if (!text || !FLAG_EMOJI_REGEX.test(text)) return;

  const fragment = document.createDocumentFragment();
  let lastIndex = 0;

  text.replace(FLAG_EMOJI_REGEX_GLOBAL, (match, index) => {
    const before = text.slice(lastIndex, index);
    if (before) fragment.appendChild(document.createTextNode(before));
    fragment.appendChild(buildFlagNode(match));
    lastIndex = index + match.length;
    return match;
  });

  const after = text.slice(lastIndex);
  if (after) fragment.appendChild(document.createTextNode(after));
  textNode.parentNode.replaceChild(fragment, textNode);
};

export const replaceFlagEmojisWithIcons = html => {
  if (
    !html ||
    !FLAG_EMOJI_REGEX.test(html) ||
    typeof document === 'undefined' ||
    !isWindowsPlatform()
  ) {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach(replaceFlagEmojisInTextNode);

  return container.innerHTML;
};

export const hasFlagEmoji = text => {
  if (!text) return false;
  return FLAG_EMOJI_REGEX.test(text);
};

export const formatFlagPreviewText = text => {
  if (!text) return '';
  const escaped = escapeHtml(text).replace(/\n/g, '<br>');
  return replaceFlagEmojisWithIcons(escaped);
};

export const formatFlagDisplayText = text => {
  if (!text) return '';
  const escaped = escapeHtml(text);
  return replaceFlagEmojisWithIcons(escaped);
};
